/**
 * 邊緣端小模型（WebLLM + Qwen2.5-0.5B）於瀏覽器內從 OCR 行文中萃取出人名。
 * 需 WebGPU；模型首次會下載並快取，之後離線可用。
 */

import type { InitProgressReport, MLCEngine } from '@mlc-ai/web-llm';

/** WebLLM 支援的輕量模型 ID（Qwen2.5-0.5B，偏中文辨識） */
export const DEFAULT_NAME_EXTRACT_MODEL_ID =
  'Qwen2.5-1.5B-Instruct-q4f16_1-MLC';

/** 快取 engine 避免重複載入 */
let cachedEngine: MLCEngine | null = null;

async function createEngine(
  modelId: string,
  initProgressCallback: (progress: InitProgressReport) => void
): Promise<MLCEngine> {
  const { CreateMLCEngine } = await import('@mlc-ai/web-llm');
  return CreateMLCEngine(modelId, {
    initProgressCallback: (progress: InitProgressReport) => {
      initProgressCallback(progress);
    },
  });
}

/**
 * 取得或建立 WebLLM engine（單例），首次呼叫會下載模型。
 *
 * @param initProgressCallback - 模型載入進度（progress 0~1、text 說明）
 */
export async function getOrCreateNameExtractEngine(
  initProgressCallback: (progress: InitProgressReport) => void
): Promise<MLCEngine> {
  if (cachedEngine) return cachedEngine;
  cachedEngine = await createEngine(
    DEFAULT_NAME_EXTRACT_MODEL_ID,
    initProgressCallback
  );
  return cachedEngine;
}

/** 用於辨識人名的 system prompt：只輸出參加者姓名，避免用詞被模型當成答案輸出 */
const NAME_EXTRACT_SYSTEM =
  'Task: From the given lines of text (from OCR), list EVERY participant name (參加者姓名). One name per line. ' +
  'Include all names you find; do not skip any. Skip only: numbers, dates, times, locations, fees, team names, titles. ' +
  'If a line has a name followed by parentheses, output only the name part. Output in the same language as the input. No explanation, only names.';

/**
 * 由邊緣端小模型從多行文字中萃取出人名。
 *
 * @param lines - OCR 拆成的一行一行文字
 * @param onProgress - 模型載入進度回呼（可選）
 * @returns 模型判斷為人名的字串陣列（每行一個）
 */
export async function extractNamesFromLinesWithLLM(
  lines: string[],
  onProgress?: (progress: InitProgressReport) => void
): Promise<string[]> {
  if (lines.length === 0) return [];

  const engine = await getOrCreateNameExtractEngine(onProgress ?? (() => {}));

  const userContent = `List every participant name in the following lines. One name per line. No other text.\n\n${lines.join('\n')}`;

  const messages: Array<{ role: 'system' | 'user'; content: string }> = [
    { role: 'system', content: NAME_EXTRACT_SYSTEM },
    { role: 'user', content: userContent },
  ];

  const reply = await engine.chat.completions.create({
    messages,
    temperature: 0.1,
    max_tokens: 1024,
  });

  const content =
    (reply.choices?.[0]?.message?.content as string | undefined)?.trim() ?? '';

  return parseNamesFromLLMOutput(content);
}

/** 明顯為 prompt 洩漏或標籤的假人名，不列入結果 */
const FAKE_NAME_PATTERNS = [
  /篩選|助手|^人名$|^name$/i,
  /[:：]\s*[^\s]+$/, // 標籤: 值 如 「篩選助手: 人名」
];

/** 正規化單一候選名稱：去編號、括號備註 */
function normalizeOne(name: string): string {
  let s = name.replace(/^\s*\d+[.)]\s*/, '').trim();
  s = s.replace(/\s*[（(].*?[）)]\s*$/g, '').trim();
  return s;
}

/**
 * 解析模型輸出：支援換行或「、」「,」分隔，每段正規化後過濾假人名。
 */
function parseNamesFromLLMOutput(output: string): string[] {
  const raw = output
    .split(/\r?\n/)
    .flatMap(line => line.split(/[、,，]/).map(s => normalizeOne(s.trim())));
  return raw.filter(name => {
    if (name.length === 0 || name.length > 50) return false;
    if (FAKE_NAME_PATTERNS.some(p => p.test(name))) return false;
    return true;
  });
}
